/**
 * EL DASHBOARD DEL NEGOCIO — NIVEL 2 (portal del dueño).
 *
 * Tres números, cómo se parten, y el calendario. El menú vive en el Layout.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'

import { Contadores, type ContadoresVista } from '../components/Contadores'
import { CupoPlantillas } from '../components/CupoPlantillas'
import { Pulso } from '../components/Pulso'
import { TendenciaPulso } from '../components/TendenciaPulso'
import { api } from '../lib/api'
import { textoAlEquipo } from '../lib/elevadas'
import { seAvisaHueco } from '../lib/hueco-observacion'
import { useFichaNegocio } from '../lib/ficha-negocio'
import { PERIODOS_PORTADA, diasDePeriodo, leerPeriodoPortada, periodoTrasSaberDemo, usePeriodoPortada } from '../lib/periodo-portada'
import { useIncluirTestsVista } from '../lib/use-incluir-tests'
import type { PulsoPeriodo } from '../lib/tipos'
import { useEsAdminUi } from '../ver-como'

interface Resumen {
    accountId: number
    businessName: string
    estadoAlta: string
    periodo: { desde: string; hasta: string }
    contadores: ContadoresVista
    pulso: PulsoPeriodo
    conversion: { numerador: number; denominador: number; tasa: number | null }
    huecoObservacion: { desdeAt: string; hastaAt: string; minutos: number } | null
    chatwootUrl: string | null
    enCola: number | null
}

interface Elevadas {
    ok: boolean
    items: { conversationId: number }[]
}

export function DashboardNegocio() {
    const { accountId } = useParams()
    const [periodo, setPeriodo] = usePeriodoPortada()
    const esAdminUi = useEsAdminUi()
    const { esDemo } = useFichaNegocio(accountId)
    const { incluirTests, listo, cambiar, mostrarInterruptor } = useIncluirTestsVista(
        accountId,
        esDemo,
        esAdminUi,
    )

    const [ajusteDemoHecho, setAjusteDemoHecho] = useState(false)
    useEffect(() => {
        if (esDemo === undefined) return
        if (esDemo) {
            const actual = leerPeriodoPortada()
            const siguiente = periodoTrasSaberDemo(true, actual)
            if (siguiente !== actual) setPeriodo(siguiente)
        }
        setAjusteDemoHecho(true)
    }, [esDemo, setPeriodo])

    const { data, isLoading, error } = useQuery<Resumen>({
        queryKey: ['business', accountId, periodo, incluirTests],
        queryFn: () =>
            api.get<Resumen>(
                `/business/${accountId}/summary?periodo=${periodo}&incluirTests=${incluirTests ? '1' : '0'}`,
            ),
        enabled: Boolean(accountId) && listo && (esDemo !== true || ajusteDemoHecho),
    })
    const elevadas = useQuery<Elevadas>({
        queryKey: ['elevadas', accountId, incluirTests],
        queryFn: () =>
            api.get<Elevadas>(
                `/business/${accountId}/elevadas?incluirTests=${incluirTests ? '1' : '0'}`,
            ),
        enabled: Boolean(accountId) && listo,
    })
    const teEsperan = elevadas.data?.ok ? elevadas.data.items.length : null

    if (!listo || isLoading || (esDemo === true && !ajusteDemoHecho)) {
        return <p className="text-sm text-neutral-500">Cargando…</p>
    }
    if (error || !data) return <p className="text-sm text-rose-400">No se pudo cargar el negocio.</p>

    const { contadores, conversion, pulso } = data
    const id = data.accountId
    const tasa = conversion.tasa !== null ? Math.round(conversion.tasa * 100) : null

    return (
        <div className="space-y-8 font-sans">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div className="min-w-0 space-y-1">
                    <h2 className="text-2xl font-semibold tracking-tight text-neutral-50">
                        Cómo va
                    </h2>
                    {tasa !== null && (
                        <p className="text-sm text-neutral-400">
                            {tasa}% de las gestiones acabaron en reserva
                            <span className="text-neutral-600">
                                {' '}
                                ({conversion.numerador} de {conversion.denominador})
                            </span>
                        </p>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <Link
                        to={`/business/${id}/calendar`}
                        className="inline-flex min-h-11 items-center rounded-lg bg-sky-600 px-3 py-1.5 text-sm font-medium text-white shadow-lg shadow-sky-900/30 transition hover:bg-sky-500 md:min-h-0"
                    >
                        Ver calendario
                    </Link>
                    {mostrarInterruptor && (
                        <button
                            onClick={() => cambiar(!incluirTests)}
                            aria-pressed={incluirTests}
                            className={
                                'min-h-11 rounded-lg border px-3 py-1.5 text-sm transition md:min-h-0 ' +
                                (incluirTests
                                    ? 'border-amber-500/60 bg-amber-500/15 text-amber-300'
                                    : 'border-neutral-800 text-neutral-500 hover:text-neutral-300')
                            }
                        >
                            {incluirTests ? 'Tests incluidos' : 'Incluir tests'}
                        </button>
                    )}
                    <div className="flex rounded-lg border border-neutral-800 p-0.5">
                        {PERIODOS_PORTADA.map((p) => (
                            <button
                                key={p.clave}
                                onClick={() => setPeriodo(p.clave)}
                                className={
                                    'min-h-11 rounded-md px-3 py-1 text-sm transition md:min-h-0 ' +
                                    (periodo === p.clave
                                        ? 'bg-neutral-800 text-neutral-100'
                                        : 'text-neutral-500 hover:text-neutral-300')
                                }
                            >
                                {p.etiqueta}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            {incluirTests && esDemo && mostrarInterruptor && (
                <p className="text-xs text-amber-400/80">Cifras de la demo: incluyen las conversaciones de prueba.</p>
            )}
            {incluirTests && !esDemo && mostrarInterruptor && (
                <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
                    Estas cifras incluyen las conversaciones de prueba. No son las que ve el dueño del negocio.
                </div>
            )}

            {periodo === 'hoy' && contadores.total === 0 && (
                <div className="rounded-xl border border-neutral-800 bg-neutral-900/60 px-4 py-3 text-sm text-neutral-300">
                    Hoy no ha llegado ninguna conversación nueva.{' '}
                    <button
                        type="button"
                        onClick={() => setPeriodo('7d')}
                        className="font-medium text-sky-400 underline-offset-2 hover:underline"
                    >
                        Ver los 7 días
                    </button>
                    {esDemo ? ' — lo que sigue en el canal suele ser de antes.' : ''}
                </div>
            )}

            {seAvisaHueco(data.huecoObservacion?.minutos) && data.huecoObservacion && (
                <div className="rounded-xl border border-amber-900/60 bg-amber-950/30 px-4 py-3 text-sm text-amber-200">
                    El panel no estuvo observando durante {data.huecoObservacion.minutos} min de este periodo.
                    Puede faltar actividad en estas cifras.
                </div>
            )}

            {teEsperan !== null && teEsperan > 0 && (
                <Link
                    to={`/business/${id}/elevadas`}
                    className="block rounded-xl border border-violet-900/50 bg-violet-950/20 px-4 py-3 text-sm text-violet-200 hover:border-violet-700/60"
                >
                    {textoAlEquipo(teEsperan)}
                </Link>
            )}

            {data.enCola !== null && data.enCola > 0 && (
                <Link
                    to={`/business/${id}/espera`}
                    className="block rounded-xl border border-orange-900/50 bg-orange-950/20 px-4 py-3 text-sm text-orange-200 hover:border-orange-700/60"
                >
                    {data.enCola === 1
                        ? 'Hay 1 persona en la lista de espera ahora.'
                        : `Hay ${data.enCola} personas en la lista de espera ahora.`}
                </Link>
            )}

            <CupoPlantillas accountId={id} />
            <Pulso datos={pulso} accountId={id} periodo={periodo} incluirTests={incluirTests} />
            <Contadores
                datos={contadores}
                accountId={id}
                periodo={periodo}
                incluirTests={incluirTests}
            />

            <TendenciaPulso accountId={id} dias={diasDePeriodo(periodo)} incluirTests={incluirTests} />
        </div>
    )
}
